import React from 'react';
import { 
  Card, CardContent, Box, Typography, Grid, TextField, MenuItem,
  Autocomplete, Rating, Paper, Divider, Chip, FormControlLabel, Checkbox
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const commonTags = [
  'Breakout', 'Reversal', 'Trend Following', 'Scalp', 'Swing',
  'Day Trade', 'Position', 'High Probability', 'Low Risk',
  'Momentum', 'Support/Resistance', 'Pattern Trade'
];

const AITradeForm = ({ tradeData, onChange, strategies, trades, metrics }) => {
  const handleChange = (field, value) => {
    onChange(field, value);
  };

  return (
    <Card elevation={3}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <TrendingUpIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Trade Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and complete your trade information
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Group 1: Basic Trade Information */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                📊 Basic Trade Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Category"
                    fullWidth
                    value={tradeData.category || 'stock'}
                    onChange={(e) => handleChange('category', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="stock">Stock</MenuItem>
                    <MenuItem value="crypto">Crypto</MenuItem>
                    <MenuItem value="forex">Forex</MenuItem>
                    <MenuItem value="derivative">Derivative</MenuItem>
                    <MenuItem value="commodity">Commodity</MenuItem>
                    <MenuItem value="index">Index</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Symbol"
                    fullWidth
                    value={tradeData.symbol || ''}
                    onChange={(e) => handleChange('symbol', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Exchange"
                    fullWidth
                    value={tradeData.exchange || ''}
                    onChange={(e) => handleChange('exchange', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Direction"
                    fullWidth
                    value={tradeData.direction || 'LONG'}
                    onChange={(e) => handleChange('direction', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="LONG">Long</MenuItem>
                    <MenuItem value="SHORT">Short</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    label="Status"
                    fullWidth
                    value={tradeData.status || 'ANALYSIS'}
                    onChange={(e) => handleChange('status', e.target.value)}
                    size="small"
                  >
                    <MenuItem value="ANALYSIS">Analysis</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="WIN">Win</MenuItem>
                    <MenuItem value="LOSS">Loss</MenuItem>
                    <MenuItem value="BREAKEVEN">Breakeven</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Trading URL"
                    fullWidth
                    value={tradeData.tradingUrl || ''}
                    onChange={(e) => handleChange('tradingUrl', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Group 2: Price Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                💰 Price Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Entry Price"
                    type="number"
                    fullWidth
                    value={tradeData.entryPrice || ''}
                    onChange={(e) => handleChange('entryPrice', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Exit Price"
                    type="number"
                    fullWidth
                    value={tradeData.exitPrice || ''}
                    onChange={(e) => handleChange('exitPrice', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Stop Loss"
                    type="number"
                    fullWidth
                    value={tradeData.stopLoss || ''}
                    onChange={(e) => handleChange('stopLoss', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Position Size"
                    type="number"
                    fullWidth
                    value={tradeData.positionSize || ''}
                    onChange={(e) => handleChange('positionSize', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Leverage"
                    type="number"
                    fullWidth
                    value={tradeData.leverage || 1}
                    onChange={(e) => handleChange('leverage', e.target.value)}
                    inputProps={{ min: 1, max: 100 }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="P&L Amount"
                    type="number"
                    fullWidth
                    value={tradeData.profitLoss || ''}
                    onChange={(e) => handleChange('profitLoss', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tradeData.enableAlerts || false}
                        onChange={(e) => handleChange('enableAlerts', e.target.checked)}
                      />
                    }
                    label="🔔 Enable Price Alerts (Buy Zone, Take Profit, Stop Loss)"
                  />
                </Grid>
                
                {metrics?.riskRewardRatio && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        📈 Risk/Reward Analysis
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            R:R Ratio
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {metrics.riskRewardRatioFormatted || '1:1'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Risk Amount
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            ${metrics.riskAmount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Reward Amount
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            ${metrics.rewardAmount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Quantity
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {metrics.quantity}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Group 3: Timing Information & Strategy */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                🕒 Timing & Strategy
              </Typography>
              <Grid container spacing={2}>
                {/* Timing Information */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Trade Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tradeData.tradeDate || ''}
                    onChange={(e) => handleChange('tradeDate', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Trade Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tradeData.tradeTime || ''}
                    onChange={(e) => handleChange('tradeTime', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Close Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tradeData.closeDate || ''}
                    onChange={(e) => handleChange('closeDate', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Close Time"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={tradeData.closeTime || ''}
                    onChange={(e) => handleChange('closeTime', e.target.value)}
                    size="small"
                  />
                </Grid>
                
                {/* Strategy & Setup */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    📈 Strategy & Setup
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={strategies}
                    getOptionLabel={(option) => option.name}
                    value={strategies.filter(s => tradeData.strategyIds?.includes(s.strategyId))}
                    onChange={(e, newVal) => handleChange('strategyIds', newVal.map(v => v.strategyId))}
                    renderInput={(params) => (
                      <TextField {...params} label="Strategy" fullWidth size="small" />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Setup"
                    fullWidth
                    value={tradeData.setup || ''}
                    onChange={(e) => handleChange('setup', e.target.value)}
                    placeholder="e.g., Bull Flag, Support Bounce, Breakout"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Group 5: Analysis & Notes */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                📝 Analysis & Notes
              </Typography>
              <TextField
                label="Insight & Analysis"
                fullWidth
                multiline
                rows={4}
                value={tradeData.insight || ''}
                onChange={(e) => handleChange('insight', e.target.value)}
                placeholder="Detailed analysis, market conditions, technical indicators, reasoning..."
                size="small"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Trade Notes & Lessons"
                fullWidth
                multiline
                rows={4}
                value={tradeData.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="What was your reasoning? Any lessons learned? Post-trade reflections..."
                size="small"
              />
            </Paper>
          </Grid>

          {/* Group 6: Rating & Tags */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                ⭐ Rating & Tags
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography component="legend" sx={{ mb: 1 }}>
                  Trade Rating
                </Typography>
                <Rating
                  value={tradeData.rating || 0}
                  onChange={(e, newVal) => handleChange('rating', newVal)}
                  size="large"
                />
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Tags</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                {(Array.isArray(tradeData.tags) ? tradeData.tags : typeof tradeData.tags === 'string' ? tradeData.tags.split(',').map(tag => tag.trim()) : []).map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => {
                      const currentTags = Array.isArray(tradeData.tags) ? tradeData.tags : typeof tradeData.tags === 'string' ? tradeData.tags.split(',').map(t => t.trim()) : [];
                      handleChange('tags', currentTags.filter((_, i) => i !== index));
                    }}
                    size="small"
                    color="primary"
                    variant="filled"
                    sx={{ borderRadius: '16px', fontWeight: 'medium' }}
                  />
                ))}
              </Box>
              <Autocomplete
                freeSolo
                options={commonTags}
                value={''}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const currentTags = Array.isArray(tradeData.tags) ? tradeData.tags : typeof tradeData.tags === 'string' ? tradeData.tags.split(',').map(t => t.trim()) : [];
                    if (!currentTags.includes(newValue)) {
                      handleChange('tags', [...currentTags, newValue]);
                    }
                  }
                }}
                renderInput={(params) => <TextField {...params} placeholder="Type and press Enter to add tags" size="small" />}
                size="small"
              />
            </Paper>
          </Grid>


        </Grid>
      </CardContent>
    </Card>
  );
};

export default AITradeForm;